import type { UseFormReturn } from 'react-hook-form';
import {
  Badge,
  Checkbox,
  Disclosure,
  DisclosureButton,
  Labeled,
  Stack,
} from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { UserBadgesSelector } from '~/components/organisms/fields/UserBadgesSelector';
import { useAppCurrentFeaturePlanQuery } from '~/lib/api/modules/Apps/hooks/useAppCurrentFeaturePlanQuery';
import { useUserQuery } from '~/lib/api/modules/Users/hooks/useUserQuery';

export type IFormBadgesSectionProps = IContainerProps & {
  form: UseFormReturn<{
    userBadges?: Array<string>;
    requireAllUserBadges?: boolean;
  }>;
  mode: IFormMode;
  appId: string;
};

export const FormBadgesSection: React.FC<IFormBadgesSectionProps> = (props) => {
  const { sx, form, appId } = props;
  const { t } = useTranslation();
  const currentFeaturePlanQuery = useAppCurrentFeaturePlanQuery({
    appId,
  });
  const userQuery = useUserQuery({});

  const isSuperAdmin = userQuery.data?.superAdmin;
  const features = currentFeaturePlanQuery.data?.features;
  const proFeature = !features?.badges;
  const allowed = !proFeature || isSuperAdmin;

  return (
    <Disclosure
      sx={sx}
      trigger={
        <DisclosureButton>
          {proFeature ? (
            <Stack horizontal gap={2}>
              {t('FormBadgesSection.title')}
              <Badge value="Pro" />
            </Stack>
          ) : (
            t('FormBadgesSection.title')
          )}
        </DisclosureButton>
      }
      disabled={!allowed}
    >
      {allowed && (
        <FormSection sub>
          <Labeled label={t('FormBadgesSection.fields.badges.label')}>
            <FormField
              name="userBadges"
              control={form.control}
              as={UserBadgesSelector}
              forwardError
              emptyValue={[]}
              appId={appId}
            />
          </Labeled>
          <Labeled
            label={t('FormBadgesSection.fields.requireAllSelected.label')}
            labelPosition="right"
          >
            <FormField
              as={Checkbox}
              control={form.control}
              name="requireAllUserBadges"
            />
          </Labeled>
        </FormSection>
      )}
    </Disclosure>
  );
};
